import React, { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import LocalizedLink, { useLocalizedPath } from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import { findProjectBySlug } from "@/data/projects";
import { trackEvent } from "@/utils/analytics";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import DeviceComposite from "@/components/DeviceComposite";
import LiveDemo from "@/components/LiveDemo";
import "./CaseStudyPage.scss";

const ExternalIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M7 3H3v10h10V9M10 2h4v4M14 2L8 8" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

const CaseStudyPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const localizedPath = useLocalizedPath();

  const project = slug ? findProjectBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  useEffect(() => {
    if (project) {
      trackEvent("case_study_open", { project: project.key });
    }
  }, [project]);

  const content = project
    ? (t(`caseStudies.${project.slug}`, { returnObjects: true }) as {
        tagline: string;
        role: string;
        year: string;
        stack: string;
        problem: string;
        approach: string[];
        outcomes: string[];
      })
    : null;

  useDocumentMeta(
    project && content
      ? {
          title: `${project.name} — Case Study | Devbloom`,
          description: content.tagline,
          canonical: `https://devbloom.dev/work/${project.slug}`,
          ogTitle: `${project.name} — Case Study | Devbloom`,
          ogDescription: content.tagline,
          ogType: "article",
          ogImage: `https://devbloom.dev/og/${project.slug}.png`,
        }
      : null
  );

  if (!project || !content) {
    return <Navigate to="/" replace />;
  }

  return (
    <article className="case-study">
      <div className="container">
        <LocalizedLink to="/#portfolio-section" className="cs-back">
          {t("caseStudy.backToWork")}
        </LocalizedLink>

        {/* Hero — the live product on its own brand color */}
        <header className="cs-hero">
          <div
            className="cs-hero-stage"
            style={{ "--brand": project.brand.bg } as React.CSSProperties}
          >
            <DeviceComposite
              project={project}
              variant="hero"
              priority
              mainClassName="cs-hero-img"
              mainAlt={t("caseStudy.screenshotAlt", { name: project.name })}
              desktopSizes="(min-width: 1280px) 760px, 66vw"
            />
          </div>
          <div className="cs-hero-text">
            <div className="cs-hero-copy">
              <div className="cs-kicker">
                <span>{content.role}</span>
                <span className="cs-kicker-dot" aria-hidden="true">·</span>
                <span>{content.year}</span>
              </div>
              <h1 className="cs-title">{project.name}</h1>
              <p className="cs-tagline">{content.tagline}</p>
              <ul className="cs-tags">
                {project.tags.map((tag) => (
                  <li className="cs-tag" key={tag}>{tag}</li>
                ))}
              </ul>
            </div>
            <div className="cs-hero-actions">
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="cs-btn cs-btn--primary"
              >
                {t("caseStudy.visitLive")} <ExternalIcon />
              </a>
            </div>
          </div>
        </header>

        {/* Live demo — only for projects that have a verified, framable demo URL */}
        {project.demo && (
          <section className="cs-section cs-demo-section">
            <LiveDemo
              url={project.demo.url}
              frame={project.demo.frame}
              projectKey={project.key}
              projectName={project.name}
              liveUrl={project.liveUrl}
              posterSrc={
                project.demo.frame === "browser" && project.screens.desktop
                  ? project.screens.desktop.fallback
                  : project.screens.mobile.fallback
              }
            />
          </section>
        )}

        {/* Stack */}
        <section className="cs-section cs-stack-section">
          <h2 className="cs-section-label">{t("caseStudy.stack")}</h2>
          <p className="cs-stack">{content.stack}</p>
        </section>

        {/* Problem */}
        <section className="cs-section">
          <h2 className="cs-section-label">{t("caseStudy.problem")}</h2>
          <p className="cs-body">{content.problem}</p>
        </section>

        {/* Approach */}
        <section className="cs-section">
          <h2 className="cs-section-label">{t("caseStudy.approach")}</h2>
          <ol className="cs-list cs-list--approach">
            {content.approach.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </section>

        {/* Outcomes */}
        <section className="cs-section">
          <h2 className="cs-section-label">{t("caseStudy.outcomes")}</h2>
          <ul className="cs-list cs-list--outcomes">
            {content.outcomes.map((outcome, i) => (
              <li key={i}>{outcome}</li>
            ))}
          </ul>
        </section>

        {/* CTA */}
        <section className="cs-cta">
          <p className="cs-cta-sub">{t("caseStudy.bookCallSub")}</p>
          <a
            href={`${localizedPath("/")}#contact-section`}
            className="cs-cta-btn"
            onClick={() => trackEvent("case_study_cta_click", { project: project.key })}
          >
            {t("caseStudy.bookCall")} <ArrowIcon />
          </a>
        </section>
      </div>
    </article>
  );
};

export default CaseStudyPage;
